import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type Map as MapLibreMap, type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { LayersIcon, Menu, MenuLabel, MenuToggle } from '@allrouted/ui/Menu';
import { routeColor } from '../lib/route-color';
import { useRoadPaths, useRoadShapes } from '../lib/roads';
import type { Board } from '../lib/types';
import { addressLine, hhmm, stopLabel } from '../lib/format';
import {
  STALLED_MESSAGE,
  emptyCollection,
  escapeHtml,
  popActions,
  setData,
  useMapInstance,
  wirePopActions,
} from './map/mapbase';
import { clearLabels, syncLabels, type MapLabel } from './map/labels';

/**
 * The dispatch map.
 *
 * A basemap is optional on purpose. Point VITE_MAP_STYLE at a self-hosted
 * Protomaps or OpenMapTiles style and you get streets; leave it unset and the
 * routes render on a plain canvas, which still answers the questions a
 * dispatcher asks -- who is where, which route is drifting, what is unassigned --
 * and keeps the system running with no external tile dependency at all.
 *
 * The instance lifecycle lives in ./map/mapbase, shared with the orders map.
 */

const NAMES_KEY = 'allrouted.map.names';

export function RouteMap({
  board,
  selectedRouteId,
  highlightStopId,
  hiddenRouteIds,
  onSelectRoute,
  onSelectStop,
  onOpenOrder,
  onUnassignOrder,
  onDeleteOrder,
  fitPreview,
}: {
  board: Board | null;
  selectedRouteId: string | null;
  /**
   * The stop the dispatcher just clicked on the timeline.
   *
   * The map pans to it, draws it large, and opens its details beside it. That
   * click used to open the order's own drawer, which answered a question nobody
   * had asked -- what a dispatcher wants first is *where* 4.3 is relative to
   * the rest of the route. The order is one more click away, from the bubble.
   */
  highlightStopId?: string | null;
  /** Routes the dispatcher has switched off; drawn nowhere. */
  hiddenRouteIds?: Set<string>;
  onSelectRoute: (routeId: string) => void;
  /**
   * A stop clicked on the map, which is the same gesture as clicking its chip
   * on the timeline and must do the same thing. Clicking the dot used to select
   * the whole route and show the brief hover tooltip, so the two halves of the
   * screen answered the same click differently -- the lane gave the customer,
   * the time, the address and a way into the order; the map gave three lines.
   */
  onSelectStop?: (routeId: string, stopId: string) => void;
  onOpenOrder?: (orderId: string, orderName: string) => void;
  /**
   * The two things a dispatcher does to a stop they did not want there.
   *
   * On the bubble because that is where they already are: finding the pin,
   * reading who has it, and then having to go to the Orders screen to take it
   * off is three screens for one decision. Delete asks first; see
   * `useOrderActions`.
   */
  onUnassignOrder?: (orderId: string, orderName: string) => void;
  onDeleteOrder?: (orderId: string, orderName: string) => void;
  /**
   * A stop that does not exist yet, drawn where Find a fit would put it: a
   * pin, and a dashed detour from the stop before to the stop after.
   */
  fitPreview?: {
    point: { lat: number; lng: number };
    from: { lat: number; lng: number } | null;
    to: { lat: number; lng: number } | null;
    color: string;
    /** What the bubble on the pin says. */
    popup?: { title: string; lines: string[] };
  } | null;
}) {
  const fittedRef = useRef(false);
  const labelsRef = useRef<Map<string, Marker>>(new Map());
  /*
   * Names are on until someone turns them off, and the choice is remembered.
   * On a dense metro day a dozen labels can crowd the stops they sit beside,
   * which is a preference rather than a default worth arguing about.
   */
  const [showNames, setShowNames] = useState(() => localStorage.getItem(NAMES_KEY) !== '0');
  /*
   * Straight lines or the road. Shared with the drawer's map, so the two
   * cannot disagree about how a route is drawn -- see lib/roads.
   */
  const [showRoads, setShowRoads] = useRoadShapes();
  const onSelectRef = useRef(onSelectRoute);
  onSelectRef.current = onSelectRoute;
  const onOpenOrderRef = useRef(onOpenOrder);
  onOpenOrderRef.current = onOpenOrder;
  const onUnassignRef = useRef(onUnassignOrder);
  onUnassignRef.current = onUnassignOrder;
  const onDeleteRef = useRef(onDeleteOrder);
  onDeleteRef.current = onDeleteOrder;
  const onSelectStopRef = useRef(onSelectStop);
  onSelectStopRef.current = onSelectStop;
  /* The bubble on the highlighted stop; one at a time, replaced not stacked. */
  const focusPopupRef = useRef<maplibregl.Popup | null>(null);
  /* The same, for an unassigned order, which has no stop to be highlighted. */
  const unassignedPopupRef = useRef<maplibregl.Popup | null>(null);

  useEffect(() => {
    const markers = labelsRef.current;
    return () => clearLabels(markers);
  }, []);

  const { containerRef, mapRef, ready, stalled } = useMapInstance((map) => {
    installLayers(map);
    fittedRef.current = false;

    map.on('click', 'stops', (event) => {
      const props = event.features?.[0]?.properties as
        | { routeId?: string; stopId?: string }
        | undefined;
      if (!props?.routeId) return;
      /*
       * The stop, not the route. Falls back to selecting the route where the
       * caller has no stop handler -- the tracking and simulation boards pass
       * one route at a time and have nothing to highlight within it.
       */
      if (props.stopId && onSelectStopRef.current) {
        onSelectStopRef.current(props.routeId, props.stopId);
      } else {
        onSelectRef.current(props.routeId);
      }
    });
    /*
     * An unassigned order is clickable too.
     *
     * It is not a stop -- there is no route and no sequence to highlight -- so
     * it gets its own bubble: what the job is, when its window is, why the
     * build left it out, and a way into the order. Before this it was the one
     * thing on the map that could not be interrogated at all, which is
     * backwards: an unrouted job is the one a dispatcher most needs to ask
     * about.
     */
    map.on('click', 'unassigned', (event) => {
      const feature = event.features?.[0];
      const p = feature?.properties as Record<string, string> | undefined;
      if (!p?.id) return;
      unassignedPopupRef.current?.remove();
      const popup = new maplibregl.Popup({
        focusAfterOpen: false,
        closeButton: true,
        closeOnClick: false,
        offset: 14,
      })
        .setLngLat((feature!.geometry as GeoJSON.Point).coordinates as [number, number])
        .setHTML(
          `<b>${escapeHtml(p.name ?? '')}</b>` +
            `<div class="map-pop-line">Not on a route</div>` +
            (p.window ? `<div class="map-pop-line">Window ${escapeHtml(p.window)}</div>` : '') +
            (p.reason ? `<div class="map-pop-line">${escapeHtml(p.reason)}</div>` : '') +
            `<button type="button" class="btn btn-small map-pop-action">Open order</button>` +
            /* No Unassign: it is not on a route, which is why it is here. */
            popActions({ unassign: false, remove: Boolean(onDeleteRef.current) }),
        )
        .addTo(map);
      wirePopActions(popup, {
        open: () => onOpenOrderRef.current?.(p.id!, p.name ?? ''),
        remove: () => onDeleteRef.current?.(p.id!, p.name ?? ''),
      });
      unassignedPopupRef.current = popup;
    });

    for (const layer of ['stops', 'unassigned']) {
      map.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    }

  });

  /*
   * The fit preview. Straight dashes rather than the road: it is a question
   * being asked, and drawing it like a committed route would answer it.
   *
   * Two legs, both arrowed so the direction reads at a glance: the drive in,
   * from wherever the driver will be coming from, drawn strong; and the drive
   * on to what they were going to do next, drawn faint. A bubble on the pin
   * says what the stop would be.
   */
  const fitPinRef = useRef<Marker | null>(null);
  const fitPopupRef = useRef<maplibregl.Popup | null>(null);
  /*
   * Keyed by content: the board re-renders on every live update, and a fresh
   * object with the same points must not pan the map back each time.
   */
  const fitKey = fitPreview ? JSON.stringify(fitPreview) : '';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    fitPinRef.current?.remove();
    fitPinRef.current = null;
    fitPopupRef.current?.remove();
    fitPopupRef.current = null;
    if (!fitPreview) {
      setData(map, 'fit-preview', emptyCollection());
      return;
    }
    const { point, from, to, color, popup } = fitPreview;
    const here: [number, number] = [point.lng, point.lat];
    const leg = (a: [number, number], b: [number, number], kind: 'in' | 'out') => ({
      type: 'Feature' as const,
      geometry: { type: 'LineString' as const, coordinates: [a, b] },
      properties: { color, kind },
    });
    setData(map, 'fit-preview', {
      type: 'FeatureCollection',
      features: [
        ...(from ? [leg([from.lng, from.lat], here, 'in')] : []),
        ...(to ? [leg(here, [to.lng, to.lat], 'out')] : []),
      ],
    });

    const el = document.createElement('div');
    el.className = 'fit-pin';
    el.style.setProperty('--pin', color);
    fitPinRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat(here).addTo(map);

    if (popup) {
      fitPopupRef.current = new maplibregl.Popup({
        focusAfterOpen: false,
        closeButton: false,
        closeOnClick: false,
        /* Above the pin's head rather than over it. */
        offset: [0, -30],
        className: 'fit-popup',
      })
        .setLngLat(here)
        .setHTML(
          `<div class="fit-pop-kicker">Prospect stop</div>` +
            `<b>${escapeHtml(popup.title)}</b>` +
            popup.lines.map((l) => `<div class="map-pop-line">${escapeHtml(l)}</div>`).join(''),
        )
        .addTo(map);
    }

    /*
     * Framed clear of the drawer. Find a fit sits over the right of the map,
     * and a plain fit put the very spot being asked about underneath it.
     */
    const coords: [number, number][] = [
      ...(from ? [[from.lng, from.lat] as [number, number]] : []),
      here,
      ...(to ? [[to.lng, to.lat] as [number, number]] : []),
    ];
    const box = map.getContainer().getBoundingClientRect();
    const drawer = document.querySelector('.drawer')?.getBoundingClientRect();
    const covered = drawer ? Math.max(0, box.right - drawer.left) : 0;
    const right = Math.min(box.width * 0.6, covered + 60);
    const bounds = new maplibregl.LngLatBounds(here, here);
    for (const c of coords) bounds.extend(c);
    map.fitBounds(bounds, {
      /* Extra at the top for the bubble. */
      padding: { top: 170, bottom: 70, left: 70, right: Math.max(70, right) },
      maxZoom: 14,
      duration: 400,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, mapRef, ready]);
  useEffect(
    () => () => {
      fitPinRef.current?.remove();
      fitPopupRef.current?.remove();
    },
    [],
  );

  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    board?.routes.forEach((route, i) => {
      /* An idle vehicle has no route and nothing to draw on a map. */
      if (route.id) map.set(route.id, routeColor(route, i));
    });
    return map;
  }, [board?.routes]);

  /*
   * Names are DOM markers, so they do not wait for the style.
   *
   * Everything else on this map is a source and a layer, which cannot be
   * installed until MapLibre has a style. Markers only need the map's
   * transform, which exists as soon as it is constructed -- so the names show
   * even when the basemap never arrives, which is exactly when a dispatcher
   * most needs to know which dot is whose.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !board) return;

    const visible = board.routes.filter((r) => r.id && !hiddenRouteIds?.has(r.id));
    /*
     * One name per technician, placed where they are.
     *
     * A live GPS fix if there is one; otherwise the stop they are working on or
     * heading to next, which is the honest answer to "where is this driver"
     * before the day has started reporting. A finished route falls back to its
     * last stop rather than vanishing.
     */
    const positionByVehicle = new Map(
      board.positions.filter((p) => p.vehicleId).map((p) => [p.vehicleId as string, p]),
    );

    const labels: MapLabel[] = visible.flatMap((route) => {
      /* `visible` has already dropped the idle lanes; this satisfies the type. */
      const routeId = route.id;
      if (!routeId) return [];
      const name = route.technicianName ?? route.vehicleName;
      const live = positionByVehicle.get(route.vehicleId);
      const located = route.stops.filter((s) => s.lat !== null && s.lng !== null);
      const anchor =
        live ??
        located.find((s) => s.status !== 'completed' && s.status !== 'failed') ??
        located[located.length - 1];
      if (!anchor) return [];

      return [
        {
          key: routeId,
          text: name,
          lat: anchor.lat as number,
          lng: anchor.lng as number,
          tone:
            route.deltaSeconds > 300 ? 'late' : route.deltaSeconds > 0 ? 'warn' : 'ok',
          onClick: () => onSelectRef.current(routeId),
        },
      ];
    });

    syncLabels(map, labelsRef.current, showNames ? labels : []);
  }, [board, hiddenRouteIds, mapRef, showNames]);

  /*
   * Taken off the board rather than passed in. The board already names the
   * territory and the day it describes, and three screens render this map --
   * two of which would have had to thread a pair of props through for no
   * reason other than this request.
   */
  const { roads, loading: loadingRoads } = useRoadPaths(
    board?.territory.id ?? null,
    board?.date ?? '',
    showRoads,
    board?.plan?.version ?? null,
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !board) return;

    /* Idle lanes have no geometry: no route, no line, no pins. */
    const visible = board.routes.filter((r) => r.id && !hiddenRouteIds?.has(r.id));

    const routeLines = {
      type: 'FeatureCollection' as const,
      features: visible
        .filter((r) => r.stops.length > 0)
        .map((route) => {
          /*
           * The road where we have it for this route, the straight line where
           * we do not. Per route rather than per map: one lane the engine could
           * not match to a road still gets drawn, and reads as the odd one out
           * rather than taking the whole map down with it.
           */
          const road = route.id ? roads?.paths[route.id] : undefined;
          const coords: [number, number][] = [];
          if (road && road.length > 1) {
            coords.push(...road);
          } else {
            if (route.startDepot) coords.push([route.startDepot.lng, route.startDepot.lat]);
            for (const stop of route.stops) {
              if (stop.lng !== null && stop.lat !== null) coords.push([stop.lng, stop.lat]);
            }
            if (route.startDepot) coords.push([route.startDepot.lng, route.startDepot.lat]);
          }
          return {
            type: 'Feature' as const,
            geometry: { type: 'LineString' as const, coordinates: coords },
            properties: {
              routeId: route.id,
              color: colorOf.get(route.id ?? '') ?? '#35b8c9',
              selected: route.id === selectedRouteId,
            },
          };
        }),
    };

    const stopPoints = {
      type: 'FeatureCollection' as const,
      features: visible.flatMap((route) =>
        route.stops
          .filter((s) => s.lat !== null && s.lng !== null)
          .map((stop) => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [stop.lng as number, stop.lat as number] },
            properties: {
              routeId: route.id,
              stopId: stop.id,
              orderId: stop.orderId,
              highlighted: stop.id === highlightStopId,
              color: colorOf.get(route.id ?? '') ?? '#35b8c9',
              /* `4.1`, the same label the timeline and the driver app use. */
              sequence: stopLabel(stop, route.routeNumber ?? route.vehicleIdx),
              name: stop.orderName,
              vehicle: route.vehicleName,
              status: stop.status,
              risk: stop.windowRisk,
              eta: hhmm(stop.actualArrival ?? stop.projectedArrival ?? stop.plannedArrival),
              done: stop.status === 'completed' || stop.status === 'failed',
              selected: route.id === selectedRouteId,
            },
          })),
      ),
    };

    const unassigned = {
      type: 'FeatureCollection' as const,
      features: board.unassigned
        .filter((o) => o.lat !== null && o.lng !== null)
        .map((order) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [order.lng as number, order.lat as number] },
          properties: {
            id: order.id,
            name: order.name,
            /* Why the last build left it out, where the build said. */
            reason: order.reason ?? '',
            window: order.timeWindows[0]
              ? `${hhmm(order.timeWindows[0].start)} – ${hhmm(order.timeWindows[0].end)}`
              : '',
          },
        })),
    };

    const depots = {
      type: 'FeatureCollection' as const,
      features: [...new Map(
        board.routes
          .filter((r) => r.startDepot)
          .map((r) => [r.startDepot!.id, r.startDepot!]),
      ).values()].map((depot) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [depot.lng, depot.lat] },
        properties: { name: depot.name },
      })),
    };

    const vehicles = {
      type: 'FeatureCollection' as const,
      features: board.positions.map((p) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        properties: { heading: p.headingDeg ?? 0 },
      })),
    };

    setData(map, 'routes', routeLines);
    setData(map, 'stops', stopPoints);
    setData(map, 'unassigned', unassigned);
    setData(map, 'depots', depots);
    setData(map, 'vehicles', vehicles);



    if (import.meta.env.DEV) {
      console.info(
        `[map] drew ${stopPoints.features.length} stops, ${routeLines.features.length} routes, ` +
          `${unassigned.features.length} unassigned, ${vehicles.features.length} vehicles`,
      );
    }

    // Frame the work once, then leave the viewport under the dispatcher's control.
    if (!fittedRef.current && stopPoints.features.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      for (const f of stopPoints.features) {
        bounds.extend(f.geometry.coordinates as [number, number]);
      }
      map.fitBounds(bounds, { padding: 64, duration: 0 });
      fittedRef.current = true;
    }
  }, [board, selectedRouteId, highlightStopId, colorOf, ready, hiddenRouteIds, roads]);

  /*
   * Pan to the clicked stop and say what it is.
   *
   * `easeTo` rather than `fitBounds`: the zoom the dispatcher chose is part of
   * what they are reading, and a click on one stop is not a reason to change
   * it. The bubble is built here rather than from the hover handler because it
   * has to stay open and carry a button, which a hover tooltip cannot.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    focusPopupRef.current?.remove();
    focusPopupRef.current = null;
    if (!highlightStopId || !board) return;

    let found: { route: Board['routes'][number]; stop: Board['routes'][number]['stops'][number] } | null = null;
    for (const route of board.routes) {
      const stop = route.stops.find((st) => st.id === highlightStopId);
      if (stop) {
        found = { route, stop };
        break;
      }
    }
    if (!found) return;
    const { route, stop } = found;
    if (stop.lat === null || stop.lng === null) return;

    map.easeTo({ center: [stop.lng, stop.lat], duration: 420 });

    const label = stopLabel(stop, route.routeNumber ?? route.vehicleIdx);
    const when = hhmm(stop.actualArrival ?? stop.projectedArrival ?? stop.plannedArrival);
    const popup = new maplibregl.Popup({ focusAfterOpen: false, closeButton: true, closeOnClick: false, offset: 14 })
      .setLngLat([stop.lng, stop.lat])
      .setHTML(
        `<b>${escapeHtml(stop.orderName)}</b>` +
          `<div class="map-pop-line">${escapeHtml(route.technicianName ?? route.vehicleName)}` +
          ` · stop ${escapeHtml(label)}</div>` +
          `<div class="map-pop-line">${stop.status === 'pending' ? 'ETA' : 'at'} ${escapeHtml(when)}` +
          `${stop.customerName ? ` · ${escapeHtml(stop.customerName)}` : ''}</div>` +
          (addressLine(stop.address)
            ? `<div class="map-pop-line">${escapeHtml(addressLine(stop.address))}</div>`
            : '') +
          `<button type="button" class="btn btn-small map-pop-action">Open order</button>` +
          popActions({ unassign: Boolean(onUnassignRef.current), remove: Boolean(onDeleteRef.current) }),
      )
      .addTo(map);

    /*
     * Wired after it is on the map, because the element does not exist until
     * then. MapLibre removes the node on close, which takes the listener with
     * it -- so there is nothing to detach beyond removing the popup itself.
     */
    wirePopActions(popup, {
      open: () => onOpenOrderRef.current?.(stop.orderId, stop.orderName),
      unassign: () => onUnassignRef.current?.(stop.orderId, stop.orderName),
      remove: () => onDeleteRef.current?.(stop.orderId, stop.orderName),
    });

    focusPopupRef.current = popup;
    return () => {
      popup.remove();
    };
  }, [highlightStopId, board, ready, mapRef]);

  return (
    <div className="map-wrap">
      <div className="map" ref={containerRef} />

      {stalled ? (
        <div className="map-stalled" role="status">
          <b>The basemap did not load</b>
          <p>Routes and stops are still planned correctly. {STALLED_MESSAGE}</p>
        </div>
      ) : null}
      {/*
        * One control for how the map is drawn, rather than one button per
        * switch. Two had already begun stacking down the corner and a third
        * would have reached the legend; behind one icon they also read as what
        * they are -- a set of drawing options, not two unrelated actions.
        */}
      <div className="map-control map-menu">
        <Menu label="Map options" icon={loadingRoads ? <span className="spinner" /> : <LayersIcon />}>
          <MenuLabel>Map</MenuLabel>
          <MenuToggle
            label="Driver names"
            hint="A label on each technician, where they are now"
            checked={showNames}
            onChange={(next) => {
              localStorage.setItem(NAMES_KEY, next ? '1' : '0');
              setShowNames(next);
            }}
          />
          <MenuToggle
            label="Follow the roads"
            hint={
              roads !== null && !roads.available
                ? 'No routing engine, so only straight lines are available'
                : 'Draw each route along the road actually driven'
            }
            checked={showRoads && !(roads !== null && !roads.available)}
            disabled={roads !== null && !roads.available}
            title={
              roads !== null && !roads.available
                ? (roads.reason ?? 'No routing engine is configured')
                : undefined
            }
            onChange={setShowRoads}
          />
        </Menu>
      </div>

      <div className="map-legend">
        <span><i style={{ background: 'var(--ok)' }} />Done</span>
        <span><i style={{ background: 'var(--accent)' }} />In progress</span>
        <span><i style={{ background: 'var(--idle)' }} />Not started</span>
        <span><i style={{ background: 'var(--late)' }} />Window at risk</span>
        {/* The swatch carries the pin's dark edge, or a white dot on a white
            legend is nothing at all. */}
        <span>
          <i style={{ background: '#ffffff', boxShadow: 'inset 0 0 0 1.5px #4d5f6b' }} />
          Unassigned
        </span>
      </div>
    </div>
  );
}

function installLayers(map: MapLibreMap): void {
  for (const id of ['routes', 'stops', 'unassigned', 'depots', 'vehicles', 'fit-preview']) {
    map.addSource(id, { type: 'geojson', data: emptyCollection() as never });
  }

  map.addLayer({
    id: 'routes',
    type: 'line',
    source: 'routes',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': ['case', ['get', 'selected'], 4, 2],
      'line-opacity': ['case', ['get', 'selected'], 0.95, 0.55],
    },
  });

  map.addLayer({
    id: 'fit-preview',
    type: 'line',
    source: 'fit-preview',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': ['case', ['==', ['get', 'kind'], 'in'], 3.5, 2.5],
      'line-opacity': ['case', ['==', ['get', 'kind'], 'in'], 1, 0.55],
      'line-dasharray': [1.5, 1.5],
    },
  });
  /*
   * Arrowheads along the dashes. Drawn on a canvas and added as an image so
   * they need nothing from the basemap style -- the map has to work with no
   * style at all, sprites included.
   */
  if (!map.hasImage('fit-arrow')) {
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(8, 6);
      ctx.lineTo(26, 16);
      ctx.lineTo(8, 26);
      ctx.lineTo(13, 16);
      ctx.closePath();
      ctx.fill();
      map.addImage('fit-arrow', ctx.getImageData(0, 0, size, size), { sdf: true });
    }
  }
  map.addLayer({
    id: 'fit-preview-arrows',
    type: 'symbol',
    source: 'fit-preview',
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 70,
      'icon-image': 'fit-arrow',
      'icon-size': ['case', ['==', ['get', 'kind'], 'in'], 0.7, 0.55],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-rotation-alignment': 'map',
    },
    paint: {
      'icon-color': ['get', 'color'],
      'icon-opacity': ['case', ['==', ['get', 'kind'], 'in'], 1, 0.6],
    },
  });

  map.addLayer({
    id: 'stops',
    type: 'circle',
    source: 'stops',
    paint: {
      /*
       * Larger than they were, and larger again for the stop being looked at.
       * At 4.2px a stop was a dot the eye slid off in a cluster of thirteen --
       * and these are click targets as well as marks.
       */
      'circle-radius': [
        'case',
        ['get', 'highlighted'], 11,
        ['get', 'selected'], 8,
        6,
      ],
      // Colour carries execution state, not route identity, when a stop is
      // finished or at risk -- state is what the dispatcher scans for.
      'circle-color': [
        'case',
        ['==', ['get', 'status'], 'completed'], '#1c7f4d',
        ['==', ['get', 'status'], 'failed'], '#b93a2b',
        ['==', ['get', 'status'], 'on_site'], '#0f6fbf',
        ['==', ['get', 'risk'], 'late'], '#b93a2b',
        ['get', 'color'],
      ],
      'circle-opacity': 0.95,
      'circle-stroke-width': [
        'case',
        ['get', 'highlighted'], 3.5,
        ['get', 'selected'], 2,
        1.4,
      ],
      /*
       * The highlighted stop takes a dark ring rather than a white one. Colour
       * on this map means execution state, so the one stop somebody asked about
       * is marked by weight and contrast instead of by being recoloured into
       * saying something about its status that is not true.
       */
      'circle-stroke-color': ['case', ['get', 'highlighted'], '#16232b', '#ffffff'],
    },
  });

  /*
   * Above the stops, deliberately.
   *
   * MapLibre paints in the order layers are added, and this one used to go in
   * before `stops`. An unrouted order four pixels from a planned one was drawn
   * and then covered by it -- present in the data, invisible on the screen, and
   * impossible to click. Two of them hid behind their neighbours on a real day
   * and read as "the map is not showing unassigned orders at all".
   *
   * Of the two, the unrouted one is what a dispatcher is hunting for, so it is
   * the one that wins the pixels. Depots and live vehicles still sit above it.
   */
  map.addLayer({
    id: 'unassigned',
    type: 'circle',
    source: 'unassigned',
    paint: {
      'circle-radius': 8,
      /*
       * White, which on both maps means "nobody has picked this up". Amber
       * read as a warning beside the red of a route at risk, when unrouted
       * work is not a fault in the day -- it is what is still to be given out.
       * The dark edge is what keeps an empty pin visible on a pale basemap.
       */
      'circle-color': '#ffffff',
      'circle-opacity': 0.95,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#4d5f6b',
    },
  });

  map.addLayer({
    id: 'depots',
    type: 'circle',
    source: 'depots',
    paint: {
      'circle-radius': 7,
      'circle-color': '#16232b',
      'circle-stroke-width': 2.4,
      'circle-stroke-color': '#ffffff',
    },
  });

  map.addLayer({
    id: 'vehicles',
    type: 'circle',
    source: 'vehicles',
    paint: {
      'circle-radius': 5.5,
      'circle-color': '#ffffff',
      'circle-stroke-width': 3,
      'circle-stroke-color': '#0f6fbf',
    },
  });

  const popup = new maplibregl.Popup({ focusAfterOpen: false, closeButton: false, closeOnClick: false, offset: 10 });

  map.on('mousemove', 'stops', (event) => {
    const feature = event.features?.[0];
    if (!feature) return;
    const p = feature.properties as Record<string, string>;
    /*
     * The pinned bubble already says all of this, in the same place. The flag
     * arrives as a string here: feature properties come back off the style as
     * JSON scalars, and a boolean set in the source reads as 'true'.
     */
    if (String(p.highlighted) === 'true') {
      popup.remove();
      return;
    }
    popup
      .setLngLat((feature.geometry as GeoJSON.Point).coordinates as [number, number])
      .setHTML(
        `<b>${escapeHtml(p.name ?? '')}</b><br>` +
          `${escapeHtml(p.vehicle ?? '')} · stop ${escapeHtml(String(p.sequence ?? ''))}<br>` +
          `<span style="color:#4d5f6b">${p.status === 'pending' ? 'ETA' : 'at'} ${p.eta}</span>`,
      )
      .addTo(map);
  });
  map.on('mouseleave', 'stops', () => popup.remove());

  map.on('mousemove', 'unassigned', (event) => {
    const feature = event.features?.[0];
    if (!feature) return;
    const p = feature.properties as Record<string, string>;
    popup
      .setLngLat((feature.geometry as GeoJSON.Point).coordinates as [number, number])
      .setHTML(
        `<b>${escapeHtml(p.name ?? '')}</b><br>` +
          `<span style="color:#4d5f6b">not on a route</span>`,
      )
      .addTo(map);
  });
  map.on('mouseleave', 'unassigned', () => popup.remove());
}
